"use client";

import React from 'react';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import { Instagram, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { usePremiumStatus } from '@/hooks/use-premium-status';

const Footer = () => {
  const locale = useLocale();
  const t = useTranslations('common.footer');
  const { isPremium, loading: premiumLoading } = usePremiumStatus();

  // Define brandStyle identical to Header
  // const brandStyle = { 
  //   fontFamily: "var(--font-marcellus)",
  //   letterSpacing: "0.03em",
  //   backgroundImage: "linear-gradient(to right, #D4B6FF, #A45DE8)",
  //   WebkitBackgroundClip: "text",
  //   WebkitTextFillColor: "transparent",
  //   textShadow: "0 0 15px rgba(164, 93, 232, 0.3)"
  // };

  return (
    <footer className="py-8 text-white" style={{ backgroundColor: '#050316' }}>
      <div className="container mx-auto px-4 md:px-6 text-center flex flex-col items-center">
        {/* Updated Logo to match Header */}
        <Link href={`/${locale}`} className="mb-4">
            <Image 
              src="/images/asktheoraclelogo.svg" 
              alt="Ask the Oracle logo - free yes/no oracle reading"
              width={188}
              height={40}
              priority
              placeholder="blur"
              blurDataURL="/images/asktheoraclelogo.svg"
              className="..."
            />
        </Link>
        
        {/* Disclaimer */}
        <p className="text-sm text-gray-400 mb-2">{t('disclaimer')}</p>

        {/* Copyright */}
        <p className="text-sm text-gray-400">{t('rights', { year: new Date().getFullYear() })}</p>

        {/* Footer Links - Structured Navigation */}
        <div className="w-full max-w-4xl mx-auto mt-8 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 text-left">
            {/* Oracle Tools Section */}
            <div>
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider" style={{ fontFamily: "var(--font-marcellus)" }}>
                {t('sections.oracleTools')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link href={`/${locale}`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.yesNoOracle')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/temple/oracle-readings/pendulum-yes-or-no`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.pendulumReading')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/temple/oracle-readings/ai-dream-interpreter`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.dreamInterpreter')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/temple/oracle-readings/oracle-card-reading`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.oracleCards')}
                  </Link>
                </li>
                <li>
                  <Link 
                    href={isPremium && !premiumLoading 
                      ? `/${locale}/temple/oracle-readings/chatinterface` 
                      : `/${locale}/pricing`} 
                    className="text-sm text-gray-400 hover:text-purple-400 transition-colors"
                  >
                    {t('links.chat')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources Section */}
            <div>
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider" style={{ fontFamily: "var(--font-marcellus)" }}>
                {t('sections.resources')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link href={`/${locale}/temple`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.temple')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/pricing`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.pricing')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/#faq`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.faq')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/tarot-cards`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('links.tarotCards')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Section */}
            <div>
              <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider" style={{ fontFamily: "var(--font-marcellus)" }}>
                {t('sections.legal')}
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link href={`/${locale}/privacy-policy`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('privacy')}
                  </Link>
                </li>
                <li>
                  <Link href={`/${locale}/terms-of-service`} className="text-sm text-gray-400 hover:text-purple-400 transition-colors">
                    {t('terms')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Social Media Icons */}
        <div className="flex space-x-4 mt-4">
            <Link href="https://www.instagram.com/asktheoracle.io/" title="Follow us on Instagram" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram className="h-6 w-6 text-gray-400 hover:text-purple-400" />
            </Link>
            <Link href="https://x.com/asktheoracleio" title="Follow us on X" target="_blank" rel="noopener noreferrer" aria-label="X">
                <X className="h-6 w-6 text-gray-400 hover:text-purple-400" />
            </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 